import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, ArrowRight, ArrowLeft } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import api from "../lib/api";
import { usePageSeo } from "../lib/seo";
import BlogValuationBanner from "../components/valuation/BlogValuationBanner";

const SITE = "https://equitashomes.com";

export default function Blog() {
  const [sp, setSp] = useSearchParams();
  const page = parseInt(sp.get("page") || "1", 10);
  const search = sp.get("search") || "";
  const category = sp.get("category") || "";
  const [data, setData] = useState(null);
  const [q, setQ] = useState(search);

  usePageSeo({
    title: "Silicon Valley Real Estate Blog | Equitas Homes",
    description: "Expert Silicon Valley real estate insights from Louis Fong — home values, seller net proceeds, transfer taxes, and keeping more equity with a 1% listing fee.",
    canonical: `${SITE}/blog`,
    jsonLd: { "@context": "https://schema.org", "@type": "Blog", "@id": `${SITE}/blog`, name: "Equitas Homes Blog", url: `${SITE}/blog`, publisher: { "@type": "RealEstateAgent", name: "Equitas Homes" } },
  });

  useEffect(() => {
    let active = true;
    setData(null);
    api.get("/blog", { params: { page, search, category, page_size: 9 } })
      .then(({ data }) => { if (active) setData(data); })
      .catch(() => { if (active) setData({ posts: [], total: 0, pages: 0, categories: [] }); });
    return () => { active = false; };
  }, [page, search, category]);

  const update = (patch) => {
    const n = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k)));
    if (!("page" in patch)) n.set("page", "1");
    setSp(n);
  };

  return (
    <div className="min-h-screen bg-brand-cream paper-grain flex flex-col">
      <Header />
      <main className="flex-1">
        <section className="bg-brand-emerald text-brand-cream">
          <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 lg:py-20">
            <span className="eyebrow">Equitas Homes · Louis Fong</span>
            <h1 className="mt-3 font-serif font-light leading-[1.06] text-4xl sm:text-5xl lg:text-6xl">
              Silicon Valley Real Estate <span className="text-brand-gold">Insights</span>
            </h1>
            <p className="mt-5 max-w-2xl text-base sm:text-lg text-brand-cream/80 leading-relaxed">
              Expert tips on selling for more and keeping your equity — home values, seller net proceeds, transfer taxes, and the 1% listing fee.
            </p>
            <form onSubmit={(e) => { e.preventDefault(); update({ search: q }); }} className="mt-8 max-w-xl flex gap-2" data-testid="blog-search-form">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-stone" />
                <input data-testid="blog-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles…"
                  className="w-full rounded-xl bg-brand-cream text-brand-ink pl-11 pr-4 py-3 outline-none focus:ring-2 focus:ring-brand-gold" />
              </div>
              <button type="submit" className="rounded-xl bg-brand-gold px-5 py-3 font-semibold text-brand-dark hover:opacity-90 transition-opacity">Search</button>
            </form>
          </div>
        </section>

        <BlogValuationBanner />

        <section className="mx-auto max-w-7xl px-5 sm:px-8 py-12">
          {data?.categories?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8" data-testid="blog-categories">
              <button onClick={() => update({ category: "" })} className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${!category ? "bg-brand-emerald text-brand-cream border-brand-emerald" : "border-brand-hairline text-brand-stone hover:border-brand-emerald"}`}>All</button>
              {data.categories.map((c) => (
                <button key={c} onClick={() => update({ category: c })} className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${category === c ? "bg-brand-emerald text-brand-cream border-brand-emerald" : "border-brand-hairline text-brand-stone hover:border-brand-emerald"}`}>{c}</button>
              ))}
            </div>
          )}

          {!data && <p className="text-brand-stone" data-testid="blog-loading">Loading articles…</p>}
          {data && data.posts.length === 0 && <p className="text-brand-stone" data-testid="blog-empty">No articles found{search ? ` for “${search}”` : ""}.</p>}

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {data?.posts?.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} data-testid="blog-card"
                className="group flex flex-col rounded-2xl bg-white border border-brand-hairline p-6 shadow-[0_4px_20px_rgba(11,30,140,0.04)] hover:border-brand-emerald transition-colors">
                {p.category && <span className="text-[0.65rem] uppercase tracking-wider text-brand-gold font-semibold">{p.category}</span>}
                <h2 className="mt-2 font-serif text-xl text-brand-ink leading-snug group-hover:text-brand-emerald transition-colors">{p.title}</h2>
                <p className="mt-3 text-sm text-brand-stone leading-relaxed line-clamp-4 flex-1">{p.excerpt}</p>
                <div className="mt-4 flex items-center justify-end text-xs text-brand-stone/70">
                  <span className="inline-flex items-center gap-1 text-brand-emerald font-medium">Read article <ArrowRight className="h-3.5 w-3.5" /></span>
                </div>
              </Link>
            ))}
          </div>

          {data && data.pages > 1 && (
            <div className="mt-12 flex items-center justify-center gap-2" data-testid="blog-pagination">
              <button disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}
                className="inline-flex items-center gap-1 rounded-lg border border-brand-hairline px-3 py-2 text-sm disabled:opacity-40 hover:border-brand-emerald"><ArrowLeft className="h-4 w-4" /> Prev</button>
              <span className="px-3 text-sm text-brand-stone">Page {page} of {data.pages}</span>
              <button disabled={page >= data.pages} onClick={() => update({ page: String(page + 1) })}
                className="inline-flex items-center gap-1 rounded-lg border border-brand-hairline px-3 py-2 text-sm disabled:opacity-40 hover:border-brand-emerald">Next <ArrowRight className="h-4 w-4" /></button>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
